import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Modal } from '../../../../shared/components';
import ImpactBadge from '../../ImpactBadge';
import {
  FinishToolkitState,
  ToolkitCalculatedValues,
  ToolkitRationale,
  ToolkitTypes,
} from '../../../../services/types/Toolkit';
import TextInput from '../../../../shared/components/form/TextInput';
import FormRadioGroup from '../../../../shared/components/form/FormRadioGroup';
import FormCheckboxGroup from '../../../../shared/components/form/FormCheckboxGroup';
import FormButtons from '../../../../shared/components/form/FormButtons';
import { BasicNotification } from '../../../../shared/components/Notification';
import FormDropdown from '../../../../shared/components/form/FormDropdown';
import RiskBadge from '../../RiskBadge';
import EsrrBadge from '../../EsrrBadge';

const CHEVRON_UP = 'chevron-up';
const CHEVRON_DOWN = 'chevron-down';

interface Props {
  calculatedValues: ToolkitCalculatedValues;
  operationNumber: string;
  visible: boolean;
  isEspf: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: (values: FinishToolkitState) => void;
  toolkitType: string;
}

export const buildFinishModalSchema = (
  t: (key: string, options?: Record<string, unknown>) => string,
  calculatedValues: ToolkitCalculatedValues,
  isEspf: boolean
) =>
  Yup.object().shape({
    comments: Yup.string()
      .trim()
      .max(750, t('validateMaxLength', { len: 750 }))
      .when('impactCategory', {
        is: (cat: string) => cat !== calculatedValues.impactCategoryData.impactCategory,
        then: (schema) => schema.concat(Yup.string().required(t('required'))),
      }),
    languages: Yup.array().min(2, t('validateLanguage')),
    rational: Yup.string().when('impactCategory', {
      is: (cat: string) => cat !== calculatedValues.impactCategoryData.impactCategory,
      then: (schema) => schema.concat(Yup.string().required(t('validateRational'))),
    }),
    disasterRiskComments: Yup.string()
      .trim()
      .max(750, t('validateMaxLength', { len: 750 }))
      .when('disasterRisk', {
        is: (cat: string) => cat !== calculatedValues.disasterRiskData.disasterRisk,
        then: (schema) => schema.concat(Yup.string().required(t('required'))),
      }),
    disasterRiskRationaleId: Yup.string().when('disasterRisk', {
      is: (cat: string) => cat !== calculatedValues.disasterRiskData.disasterRisk,
      then: (schema) => schema.concat(Yup.string().required(t('validateRational'))),
    }),
    esrrComments: Yup.string()
      .trim()
      .max(750, t('validateMaxLength', { len: 750 }))
      .when('esrr', {
        is: (cat: string) => cat !== calculatedValues.esrrData.esrr && isEspf === true,
        then: (schema) =>
          schema.concat(
            Yup.string()
              .notOneOf([calculatedValues.esrrData.justification], t('updateOverall'))
              .required(t('required'))
          ),
        otherwise: (schema) => schema.concat(Yup.string().nullable()),
      }),
    esrrRationaleId: Yup.string().when('esrr', {
      is: (cat: string) => cat !== calculatedValues.esrrData.esrr,
      then: (schema) => schema.concat(Yup.string().required(t('validateRational'))),
    }),
  });

const FinishModal = (props: Props) => {
  const {
    isEspf,
    calculatedValues,
    operationNumber,
    visible,
    setVisible,
    handleSubmit,
    toolkitType,
  } = props;
  const { t } = useTranslation();
  const [changingCategory, setChangingCategory] = useState(false);
  const [changingDisasterRisk, setChangingDisasterRisk] = useState(false);
  const [changingEsrr, setChangingEsrr] = useState(false);
  const chevron = changingCategory ? CHEVRON_UP : CHEVRON_DOWN;
  const chevronDisasterRisk = changingDisasterRisk ? CHEVRON_UP : CHEVRON_DOWN;
  const chevronEsrr = changingEsrr ? CHEVRON_UP : CHEVRON_DOWN;

  // const isFI = calculatedValues.impactCategoryData.impactCategory === 'FI';

  const ESIC_FILTERS = {
    A: 'elevate',
    C: 'reduce',
  } as const;

  const DISASTERRISK_FILTERS = {
    High: 'elevate',
    Low: 'reduce',
  } as const;

  const categoryRadioItems = [
    {
      id: 2,
      text: 'A',
      disabled:
        calculatedValues.impactCategoryData.impactCategory === 'A' ||
        (isEspf && toolkitType === ToolkitTypes.LBR),
      value: 'A',
    },
    {
      id: 1,
      text: 'B',
      disabled: calculatedValues.impactCategoryData.impactCategory === 'B',
      value: 'B',
    },
    {
      id: 3,
      text: 'C',
      disabled: calculatedValues.impactCategoryData.impactCategory === 'C',
      value: 'C',
    },
    {
      id: 4,
      text: 'B13',
      disabled: calculatedValues.impactCategoryData.impactCategory === 'B13',
      value: 'B13',
      type: 'Classic',
    },
  ];

  const disasterRiskRadioItems = [
    {
      id: 3,
      text: 'High',
      disabled: calculatedValues.disasterRiskData.disasterRisk === 'High',
      value: 'High',
    },
    {
      id: 2,
      text: 'Moderate',
      disabled: calculatedValues.disasterRiskData.disasterRisk === 'Moderate',
      value: 'Moderate',
    },
    {
      id: 1,
      text: 'Low',
      disabled: calculatedValues.disasterRiskData.disasterRisk === 'Low',
      value: 'Low',
    },
  ];

  const esrrRadioItems = [
    {
      id: 4,
      text: 'High',
      disabled:
        calculatedValues.esrrData.esrr === '4High' || (isEspf && toolkitType === ToolkitTypes.LBR),
      value: '4High',
    },
    {
      id: 3,
      text: 'Substantial',
      disabled: calculatedValues.esrrData.esrr === '3Substantial',
      value: '3Substantial',
    },
    {
      id: 2,
      text: 'Moderate',
      disabled: calculatedValues.esrrData.esrr === '2Moderate',
      value: '2Moderate',
    },
    {
      id: 1,
      text: 'Low',
      disabled: calculatedValues.esrrData.esrr === '1Low',
      value: '1Low',
    },
  ];

  const formStateInitial = {
    impactCategory: calculatedValues.impactCategoryData.impactCategory,
    rational: '',
    comments: '',
    languages: isEspf ? ['none', 'English'] : ['none'],
    updateCompliance: false,
    disasterRisk: calculatedValues.disasterRiskData.disasterRisk,
    disasterRiskRationaleId: '',
    disasterRiskComments: '',
    esrr: calculatedValues.esrrData.esrr,
    esrrRationaleId: '',
    esrrComments: calculatedValues.esrrData.justification,
  };

  const languageOptions = [
    { id: 1, text: 'English', value: 'English' },
    { id: 2, text: 'Spanish', value: 'Spanish', disabled: true },
  ];

  const rationalElements = (rationales: ToolkitRationale[]) =>
    rationales.map((x) => ({ id: x.id, text: x.name, value: x.id.toString() }));

  const handleChangingCategory = () => {
    setChangingCategory((current) => !current);
  };

  const handleChangingDisasterRisk = () => {
    setChangingDisasterRisk((_current) => !_current);
  };

  const handleChangingEsrr = () => {
    setChangingEsrr((_current) => !_current);
  };

  return (
    <Modal
      title={isEspf ? t('finishSF') : t('finishPdf')}
      visible={visible}
      setVisible={setVisible}
    >
      <Formik
        initialValues={formStateInitial}
        validationSchema={buildFinishModalSchema(t, calculatedValues, isEspf)}
        onSubmit={(values) => {
          values.languages.splice(0, 1); // delete 'none' value from array
          handleSubmit({
            ...values,
            rational: parseInt(values.rational, 10),
            disasterRiskRationaleId: parseInt(values.disasterRiskRationaleId, 10),
            esrrRationaleId: parseInt(values.esrrRationaleId, 10),
          });
        }}
      >
        {(formik) => {
          const esicRationalOptions = useMemo(() => {
            const { impactCategory } = formik.values;
            const impactCategoryCalculated = calculatedValues.impactCategoryData.impactCategory;
            const keyword =
              impactCategory in ESIC_FILTERS && impactCategoryCalculated === 'B'
                ? ESIC_FILTERS[impactCategory as keyof typeof ESIC_FILTERS]
                : undefined;

            const list = calculatedValues.impactCategoryData.toolkitRationales;

            const filtered =
              keyword && impactCategory !== impactCategoryCalculated
                ? list.filter((x) => x.name && x.name.toLowerCase().includes(keyword))
                : list;

            return rationalElements(filtered);
          }, [formik.values.impactCategory, calculatedValues.impactCategoryData.toolkitRationales]);

          useEffect(() => {
            if (
              formik.values.rational &&
              !esicRationalOptions.some((x) => x.value === formik.values.rational)
            ) {
              formik.setFieldValue('rational', '');
            }
          }, [esicRationalOptions]);

          const disasterRiskRationalOptions = useMemo(() => {
            const { disasterRisk } = formik.values;
            const disasterRiskCalculated = calculatedValues.disasterRiskData.disasterRisk;
            const keyword =
              disasterRiskCalculated === 'Moderate' && disasterRisk in DISASTERRISK_FILTERS
                ? DISASTERRISK_FILTERS[disasterRisk as keyof typeof DISASTERRISK_FILTERS]
                : undefined;

            const list = calculatedValues.disasterRiskData.toolkitRationales;

            const filtered = keyword
              ? list.filter((x) => x.name && x.name.toLowerCase().includes(keyword))
              : list;

            return rationalElements(filtered);
          }, [formik.values.disasterRisk, calculatedValues.disasterRiskData.toolkitRationales]);

          useEffect(() => {
            if (
              formik.values.rational &&
              !disasterRiskRationalOptions.some((x) => x.value === formik.values.rational)
            ) {
              formik.setFieldValue('disasterRiskRationaleId', '');
            }
          }, [disasterRiskRationalOptions]);

          const esrrRationalOptions = useMemo(() => {
            const { esrr } = formik.values;
            const esrrCalculated = calculatedValues.esrrData.esrr;
            const reduceValues =
              (esrrCalculated === '2Moderate' && esrr === '1Low') ||
              (esrrCalculated === '3Substantial' && (esrr === '2Moderate' || esrr === '1Low')) ||
              esrrCalculated === '4High';

            const keyword = reduceValues ? 'reduce' : 'elevate';

            const list = calculatedValues.esrrData.toolkitRationales;

            const filtered =
              keyword && esrr !== esrrCalculated
                ? list.filter((x) => x.name && x.name.toLowerCase().includes(keyword))
                : list;

            return rationalElements(filtered);
          }, [formik.values.esrr, calculatedValues.esrrData.toolkitRationales]);

          useEffect(() => {
            if (
              formik.values.rational &&
              !esrrRationalOptions.some((x) => x.value === formik.values.rational)
            ) {
              formik.setFieldValue('esrrRationaleId', '');
            }
          }, [esrrRationalOptions]);

          return (
            <form className="form" onSubmit={formik.handleSubmit}>
              <div className="modal__body">
                <div className="finish-modal">
                  {calculatedValues.impactCategoryData.message && (
                    <div className="modal_notification">
                      <BasicNotification
                        message={calculatedValues.impactCategoryData.message}
                        color="warning"
                        icon="exclamation-triangle"
                      />
                    </div>
                  )}
                  <div className="finish-modal_impact-info mb-2">
                    <p>{t('operationClassified', { operationNumber })}</p>
                  </div>
                  {isEspf && (
                    <>
                      <div className="finish-modal_impact-info">
                        <p>{t('applicableESPS', { operationNumber })}:</p>
                      </div>
                      <div className="finish-modal_impact-info mb-2">
                        <p>
                          <b>
                            {calculatedValues.esps ? calculatedValues.esps : t('noApplicableESPS')}
                          </b>
                        </p>
                      </div>
                    </>
                  )}
                  <div className="finish-modal_impact-info mb-2">
                    <p>{t('IsClassified')}:</p>
                  </div>
                  <div className="finish-modal_impact-info mb-2">
                    <p>
                      <b>{isEspf ? t('ESIC') : t('impactCategory')}</b>
                    </p>
                    {calculatedValues.impactCategoryData.impactCategory === 'N/A' ? (
                      'N/A'
                    ) : (
                      <ImpactBadge text={calculatedValues.impactCategoryData.impactCategory} />
                    )}
                  </div>
                  {(toolkitType === ToolkitTypes.LBR &&
                    calculatedValues.impactCategoryData.impactCategory === 'N/A') ||
                    (toolkitType !== ToolkitTypes.FI &&
                      toolkitType !== ToolkitTypes.TCPO &&
                      toolkitType !== ToolkitTypes.PBL && (
                        <div className="mb-2">
                          <Button
                            className="btn-tertiary"
                            iconRight={chevron}
                            text={isEspf ? t('changeESIC') : t('changeCategory')}
                            type="button"
                            onClick={handleChangingCategory}
                          />
                          {changingCategory && (
                            <div className="category-selection-panel">
                              <p>{isEspf ? t('whichESIC') : t('whichCategory')}</p>
                              <FormRadioGroup
                                name="impactCategory"
                                elements={categoryRadioItems.filter(
                                  (x) =>
                                    x.type === undefined || x.type === (isEspf ? 'Espf' : 'Classic')
                                )}
                              />
                              <FormDropdown
                                label={t('rationalJustification')}
                                name="rational"
                                elements={esicRationalOptions}
                                value={formik.values.rational}
                                className="no-modal"
                              />
                              <TextInput
                                {...formik}
                                name="comments"
                                placeholder={
                                  isEspf ? t('rationaleESICPlaceholder') : t('rationalePlaceholder')
                                }
                                label={t('comments')}
                                type="textarea"
                                disabled={
                                  formik.values.impactCategory ===
                                  calculatedValues.impactCategoryData.impactCategory
                                }
                              />
                            </div>
                          )}
                        </div>
                      ))}
                  {toolkitType === ToolkitTypes.PBL && (
                    <div
                      className="notification-wrapper-100 d-flex justify-content-center mb-3"
                      style={{ fontSize: '13px' }}
                    >
                      <BasicNotification
                        message={t('changeESICPbl')}
                        color="warning"
                        icon="exclamation-triangle"
                      />
                    </div>
                  )}
                  <div className="finish-modal_impact-info mb-2">
                    <p>
                      <b>{isEspf ? t('DCCRC') : t('disasterRisk')}</b>
                    </p>
                    {formik.values.impactCategory === 'B13' ? (
                      'N/A'
                    ) : (
                      <RiskBadge text={calculatedValues.disasterRiskData.disasterRisk} />
                    )}
                  </div>
                  {toolkitType !== ToolkitTypes.PBL && toolkitType !== ToolkitTypes.TCPO && (
                    <div className="mb-2">
                      <Button
                        className="btn-tertiary"
                        iconRight={chevronDisasterRisk}
                        text={isEspf ? t('changeDCCRC') : t('changeDisasterRisk')}
                        type="button"
                        onClick={handleChangingDisasterRisk}
                        disabled={formik.values.impactCategory === 'B13'}
                      />
                      {changingDisasterRisk && formik.values.impactCategory !== 'B13' && (
                        <div className="category-selection-panel">
                          <p>{isEspf ? t('whichDCCRC') : t('whichDisasterRisk')}</p>
                          <FormRadioGroup name="disasterRisk" elements={disasterRiskRadioItems} />
                          <FormDropdown
                            label={t('rationalJustification')}
                            name="disasterRiskRationaleId"
                            elements={disasterRiskRationalOptions}
                            value={formik.values.disasterRiskRationaleId}
                            className="no-modal"
                          />
                          <TextInput
                            {...formik}
                            name="disasterRiskComments"
                            placeholder={
                              isEspf
                                ? t('rationaleDCCRCPlaceholder')
                                : t('rationaleDisasterRiskPlaceholder')
                            }
                            label={t('comments')}
                            type="textarea"
                            disabled={
                              formik.values.disasterRisk ===
                              calculatedValues.disasterRiskData.disasterRisk
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {isEspf && (
                    <div className="finish-modal_impact-info mb-2">
                      <p>
                        <b>{t('ESRR')}</b>
                      </p>
                      {calculatedValues.esrrData.esrr ? (
                        <EsrrBadge text={calculatedValues.esrrData.esrr} />
                      ) : (
                        'N/A'
                      )}
                    </div>
                  )}
                  {isEspf &&
                    calculatedValues.esrrData.esrr &&
                    calculatedValues.esrrData.lastCompass && (
                      <div
                        className="notification-wrapper-100 d-flex justify-content-center mb-3"
                        style={{ fontSize: '13px' }}
                      >
                        <BasicNotification
                          message={
                            calculatedValues.esrrData.changes
                              ? t('changesEsrr')
                              : t('noChangesEsrr')
                          }
                          color="warning"
                          icon="exclamation-triangle"
                        />
                      </div>
                    )}
                  {isEspf && calculatedValues.esrrData.esrr && (
                    <div className="mb-2">
                      <Button
                        className="btn-tertiary"
                        iconRight={chevronEsrr}
                        text={t('changeEsrr')}
                        type="button"
                        onClick={handleChangingEsrr}
                        disabled={formik.values.impactCategory === 'B13'}
                      />
                      {changingEsrr && formik.values.impactCategory !== 'B13' && (
                        <div className="category-selection-panel">
                          <p>{t('whichEsrr')}</p>
                          <FormRadioGroup name="esrr" elements={esrrRadioItems} />
                          <FormDropdown
                            label={t('rationalJustification')}
                            name="esrrRationaleId"
                            elements={esrrRationalOptions}
                            value={formik.values.esrrRationaleId}
                            className="no-modal"
                          />
                          <TextInput
                            {...formik}
                            name="esrrComments"
                            placeholder={t('rationaleEsrrPlaceholder')}
                            label={t('esrrJustification')}
                            type="textarea"
                            disabled={formik.values.esrr === calculatedValues.esrrData.esrr}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {!isEspf && (
                    <div className="language-selection-panel">
                      <p>{t('languageSelectionLabel')}:</p>
                      <FormCheckboxGroup name="languages" elements={languageOptions} />
                      {formik.touched.languages && formik.errors.languages && (
                        <span className="form__form-group-error">{formik.errors.languages}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <FormButtons
                onCancel={() => {
                  setChangingCategory(false);
                  setChangingDisasterRisk(false);
                  setVisible(false);
                }}
                submitText={isEspf ? t('createSF') : t('createPdf')}
                notDisabled={isEspf}
              />
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default FinishModal;
